import { getCountry } from '../lib/countries.js'

/**
 * Flag + country name for a showcase project, shown above the title so a
 * visitor can see at a glance which market the work was built for.
 *
 * Renders nothing when the project has no country set, so projects added
 * before this field existed simply keep their old layout.
 *
 * `tone` picks the palette: "dark" for the near-black portfolio and detail
 * pages, "light" for the white homepage spotlight.
 */
export default function CountryTag({ code, tone = 'dark', className = '' }) {
  const country = getCountry(code)
  if (!country) return null

  /* The dark tone names its colour as a literal rather than `text-slate-300`.
     The homepage wraps itself in `.home-light`, whose sheet rewrites every
     `text-slate-*` to a dark ink with `!important` — so inside the project
     modal, which is dark, the label was being repainted slate-700 on a near
     black card and disappearing. An arbitrary value is not in that sheet's
     selector list, so it survives. The hex is what slate-300 resolves to. */
  const palette =
    tone === 'light'
      ? 'border-slate-200 bg-white text-slate-600'
      : 'border-white/12 bg-white/[0.06] text-[#cbd5e1]'

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 ${palette} ${className}`}
    >
      <img
        src={country.flag}
        alt=""
        width={18}
        height={13}
        loading="lazy"
        /* A flag on a white ground needs an edge or it floats; the ring is
           faint enough not to read as a border on the dark pages. */
        className="h-[13px] w-[18px] shrink-0 rounded-[2px] object-cover ring-1 ring-black/10"
      />
      <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.12em]">
        {country.name}
      </span>
      <span className="sr-only">— country this project was built for</span>
    </span>
  )
}
